import React, { useState } from 'react';
import ToolPage from '@/components/ToolPage';
import { Field, inputClass } from '@/components/calcUi';
import { getToolBySlug } from '@/lib/tools';
import { useCurrency, formatMoney, formatNumber } from '@/lib/currency';

const num = (v) => (v === '' || v === null || v === undefined || isNaN(Number(v)) ? NaN : Number(v));

// ========================= Discount =========================
export function DiscountCalculator() {
  const tool = getToolBySlug('discount-calculator');
  const { currency } = useCurrency();
  const [price, setPrice] = useState('120');
  const [pct, setPct] = useState('25');
  const P = num(price), D = num(pct);
  const valid = !isNaN(P) && !isNaN(D);
  const saved = valid ? (P * D) / 100 : NaN;
  const final = valid ? P - saved : NaN;
  return (
    <ToolPage
      tool={tool}
      intro={<p>Turn a sticker price and a percent-off tag into the actual price you'll pay at checkout.</p>}
      formula={<>Discount = Price × (Percent / 100),  Final = Price − Discount</>}
      examples={[
        `${formatMoney(120, currency)} at 25% off saves ${formatMoney(30, currency)} → final ${formatMoney(90, currency)}.`,
        `A ${formatMoney(59.99, currency)} shirt at 40% off is ${formatMoney(35.99, currency)}.`,
      ]}
      faqs={[
        { q: 'Does this include sales tax or VAT?', a: 'No — this is a pre-tax price. Different countries and regions add tax differently, so we deliberately keep the discount separate.' },
        { q: 'What if the discount stacks with another?', a: 'Discounts usually stack multiplicatively. Apply them one after another: 20% off, then a 10% coupon, is 0.8 × 0.9 = 0.72, i.e. 28% total off, not 30%.' },
      ]}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="grid grid-cols-2 gap-4 content-start">
          <Field label={`Original price (${currency.symbol})`} htmlFor="disc-price">
            <input id="disc-price" data-testid="disc-price" type="number" step="any" value={price} onChange={(e) => setPrice(e.target.value)} className={inputClass} />
          </Field>
          <Field label="Discount (%)" htmlFor="disc-pct">
            <input id="disc-pct" data-testid="disc-pct" type="number" step="any" value={pct} onChange={(e) => setPct(e.target.value)} className={inputClass} />
          </Field>
        </div>
        <div className="result-panel rounded-xl p-6 space-y-2">
          <div className="text-xs uppercase tracking-wide text-slate-500">Final price</div>
          <div className="text-4xl font-bold text-emerald-700" data-testid="disc-final">{formatMoney(final, currency)}</div>
          <div className="text-sm text-slate-600" data-testid="disc-saved">You save {formatMoney(saved, currency)}</div>
        </div>
      </div>
    </ToolPage>
  );
}

// ========================= Salary =========================
export function SalaryCalculator() {
  const tool = getToolBySlug('salary-calculator');
  const { currency } = useCurrency();
  const [amount, setAmount] = useState('60000');
  const [basis, setBasis] = useState('annual');
  const [hpw, setHpw] = useState('40');
  const [wpy, setWpy] = useState('52');
  const A = num(amount), H = num(hpw), W = num(wpy);
  const totalHours = H * W;
  let annual = NaN;
  if (!isNaN(A) && !isNaN(totalHours) && totalHours > 0) {
    if (basis === 'annual') annual = A;
    if (basis === 'monthly') annual = A * 12;
    if (basis === 'weekly') annual = A * W;
    if (basis === 'hourly') annual = A * totalHours;
  }
  const monthly = annual / 12, weekly = annual / W, hourly = annual / totalHours;
  return (
    <ToolPage
      tool={tool}
      intro={<p>Compare gross pay across common time frames. Adjust hours per week and weeks per year to match your working pattern (default: 40 hours × 52 weeks).</p>}
      formula={<>Annual = amount converted by chosen basis; hourly = Annual / (hours per week × weeks per year).</>}
      examples={[
        `An annual salary of ${formatMoney(60000, currency)} at 40h/week × 52w = ${formatMoney(28.85, currency)}/h.`,
        `An hourly rate of ${formatMoney(20, currency)} at 40h/week × 48w (with 4 weeks off) = ${formatMoney(38400, currency)}/yr.`,
      ]}
      faqs={[
        { q: 'Is this before or after tax?', a: 'Before tax (gross). Tax rules differ by country and personal circumstances, so we deliberately leave net-of-tax to a specialist calculator.' },
        { q: 'Which weeks-per-year should I use?', a: '52 is the calendar default. If you take unpaid holiday, use a smaller number (e.g. 48) to get a more realistic hourly figure.' },
      ]}
      limitations={<>Country-specific taxes, social contributions, and benefits are not included. Consult a local tax adviser for take-home pay.</>}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label={`Amount (${currency.symbol})`} htmlFor="sal-amount">
              <input id="sal-amount" data-testid="sal-amount" type="number" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} />
            </Field>
            <Field label="Basis" htmlFor="sal-basis">
              <select id="sal-basis" data-testid="sal-basis" value={basis} onChange={(e) => setBasis(e.target.value)} className={inputClass}>
                <option value="annual">Per year</option>
                <option value="monthly">Per month</option>
                <option value="weekly">Per week</option>
                <option value="hourly">Per hour</option>
              </select>
            </Field>
            <Field label="Hours / week" htmlFor="sal-hpw">
              <input id="sal-hpw" data-testid="sal-hpw" type="number" step="any" value={hpw} onChange={(e) => setHpw(e.target.value)} className={inputClass} />
            </Field>
            <Field label="Weeks / year" htmlFor="sal-wpy">
              <input id="sal-wpy" data-testid="sal-wpy" type="number" step="any" value={wpy} onChange={(e) => setWpy(e.target.value)} className={inputClass} />
            </Field>
          </div>
        </div>
        <div className="result-panel rounded-xl p-6 space-y-2 text-sm">
          <Row label="Per year" value={formatMoney(annual, currency)} primary testId="sal-annual" />
          <Row label="Per month" value={formatMoney(monthly, currency)} testId="sal-monthly" />
          <Row label="Per week" value={formatMoney(weekly, currency)} testId="sal-weekly" />
          <Row label="Per hour" value={formatMoney(hourly, currency)} testId="sal-hourly" />
        </div>
      </div>
    </ToolPage>
  );
}
function Row({ label, value, primary, testId }) {
  return (
    <div className={`flex justify-between items-baseline ${primary ? 'border-b border-slate-200 pb-2 mb-1' : ''}`}>
      <span className="text-slate-500">{label}</span>
      <span data-testid={testId} className={primary ? 'text-2xl font-bold text-emerald-700' : 'font-semibold text-slate-900'}>{value}</span>
    </div>
  );
}

// ========================= Savings =========================
export function SavingsCalculator() {
  const tool = getToolBySlug('savings-calculator');
  const { currency } = useCurrency();
  const [start, setStart] = useState('1000');
  const [monthly, setMonthly] = useState('200');
  const [rate, setRate] = useState('5');
  const [years, setYears] = useState('10');
  const P = num(start), C = num(monthly), r = num(rate), Y = num(years);
  const valid = ![P,C,r,Y].some(isNaN) && Y >= 0;
  const n = Math.round((Y || 0) * 12);
  const i = (r || 0) / 100 / 12;
  let fv = NaN;
  if (valid) {
    fv = P * Math.pow(1 + i, n) + (i === 0 ? C * n : C * ((Math.pow(1 + i, n) - 1) / i));
  }
  const contributed = valid ? P + C * n : NaN;
  const earned = valid ? fv - contributed : NaN;
  return (
    <ToolPage
      tool={tool}
      intro={<p>Project a savings goal with a starting balance, monthly deposits, and a nominal annual interest rate compounded monthly.</p>}
      formula={<>FV = P·(1 + i)ⁿ + C·[(1 + i)ⁿ − 1] / i,  where i = annual rate / 12,  n = years × 12.</>}
      examples={[
        `Starting with ${formatMoney(1000, currency)} plus ${formatMoney(200, currency)}/month at 5% for 10 years → about ${formatMoney(32800, currency)}.`,
        `${formatMoney(0, currency)} start, ${formatMoney(500, currency)}/month at 4% for 20 years → about ${formatMoney(183000, currency)}.`,
      ]}
      faqs={[
        { q: 'Is inflation included?', a: 'No. The rate is a nominal interest rate. To see "real" purchasing power, subtract your expected inflation rate from the interest rate.' },
        { q: 'Are deposits made at the start or end of the month?', a: 'End of month (ordinary annuity). If deposits happen at the start, the true balance is slightly higher.' },
      ]}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="grid grid-cols-2 gap-4 content-start">
          <Field label={`Starting balance (${currency.symbol})`} htmlFor="sav-start"><input id="sav-start" data-testid="sav-start" type="number" step="any" value={start} onChange={(e) => setStart(e.target.value)} className={inputClass} /></Field>
          <Field label={`Monthly deposit (${currency.symbol})`} htmlFor="sav-monthly"><input id="sav-monthly" data-testid="sav-monthly" type="number" step="any" value={monthly} onChange={(e) => setMonthly(e.target.value)} className={inputClass} /></Field>
          <Field label="Annual rate (%)" htmlFor="sav-rate"><input id="sav-rate" data-testid="sav-rate" type="number" step="any" value={rate} onChange={(e) => setRate(e.target.value)} className={inputClass} /></Field>
          <Field label="Years" htmlFor="sav-years"><input id="sav-years" data-testid="sav-years" type="number" step="any" value={years} onChange={(e) => setYears(e.target.value)} className={inputClass} /></Field>
        </div>
        <div className="result-panel rounded-xl p-6 space-y-2 text-sm">
          <Row label="Future value" value={formatMoney(fv, currency)} primary testId="sav-fv" />
          <Row label="You contributed" value={formatMoney(contributed, currency)} testId="sav-contributed" />
          <Row label="Interest earned" value={formatMoney(earned, currency)} testId="sav-earned" />
        </div>
      </div>
    </ToolPage>
  );
}

// ========================= Interest =========================
export function InterestCalculator() {
  const tool = getToolBySlug('interest-calculator');
  const { currency } = useCurrency();
  const [P, setP] = useState('10000');
  const [rate, setRate] = useState('6');
  const [years, setYears] = useState('5');
  const [mode, setMode] = useState('compound');
  const [comp, setComp] = useState('12');
  const p = num(P), r = num(rate) / 100, y = num(years), n = num(comp);
  let total = NaN, interest = NaN;
  if (![p,r,y].some(isNaN)) {
    if (mode === 'simple') { interest = p * r * y; total = p + interest; }
    else if (!isNaN(n) && n > 0) { total = p * Math.pow(1 + r / n, n * y); interest = total - p; }
  }
  return (
    <ToolPage
      tool={tool}
      intro={<p>Compare simple and compound interest side by side. Compound interest reinvests the interest earned; simple interest does not.</p>}
      formula={<>
        <div>Simple:  A = P (1 + r·t)</div>
        <div>Compound: A = P (1 + r/n)^(n·t)</div>
      </>}
      examples={[
        `${formatMoney(10000, currency)} at 6% compounded monthly for 5 years → about ${formatMoney(13489, currency)}.`,
        `Same amount at 6% simple interest for 5 years → exactly ${formatMoney(13000, currency)}.`,
      ]}
      faqs={[
        { q: 'What does "compounded monthly" mean?', a: 'Interest is calculated 12 times a year and added back to the balance, so future interest is earned on both the principal and the accumulated interest.' },
        { q: 'Which should I use?', a: 'Most savings accounts, bonds, and loans quote a rate but compound at some interval (usually monthly). Use compound to reflect real accounts.' },
      ]}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="flex gap-2">
            {['compound','simple'].map((k) => (
              <button key={k} type="button" onClick={() => setMode(k)} data-testid={`int-mode-${k}`}
                className={`flex-1 h-11 rounded-lg text-sm font-medium capitalize ${mode === k ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>
                {k}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={`Principal (${currency.symbol})`} htmlFor="int-p"><input id="int-p" data-testid="int-p" type="number" step="any" value={P} onChange={(e) => setP(e.target.value)} className={inputClass} /></Field>
            <Field label="Annual rate (%)" htmlFor="int-r"><input id="int-r" data-testid="int-r" type="number" step="any" value={rate} onChange={(e) => setRate(e.target.value)} className={inputClass} /></Field>
            <Field label="Years" htmlFor="int-y"><input id="int-y" data-testid="int-y" type="number" step="any" value={years} onChange={(e) => setYears(e.target.value)} className={inputClass} /></Field>
            {mode === 'compound' && (
              <Field label="Compounds / year" htmlFor="int-n">
                <select id="int-n" data-testid="int-n" value={comp} onChange={(e) => setComp(e.target.value)} className={inputClass}>
                  <option value="1">Annually (1)</option>
                  <option value="2">Semi-annually (2)</option>
                  <option value="4">Quarterly (4)</option>
                  <option value="12">Monthly (12)</option>
                  <option value="365">Daily (365)</option>
                </select>
              </Field>
            )}
          </div>
        </div>
        <div className="result-panel rounded-xl p-6 space-y-2 text-sm">
          <Row label="Total value" value={formatMoney(total, currency)} primary testId="int-total" />
          <Row label="Interest earned" value={formatMoney(interest, currency)} testId="int-earned" />
        </div>
      </div>
    </ToolPage>
  );
}

// ========================= Loan =========================
export function LoanCalculator() {
  const tool = getToolBySlug('loan-calculator');
  const { currency } = useCurrency();
  const [amt, setAmt] = useState('20000');
  const [rate, setRate] = useState('7');
  const [years, setYears] = useState('5');
  const A = num(amt), R = num(rate) / 100 / 12, Y = num(years), N = Y * 12;
  const valid = ![A,R,Y].some(isNaN) && A > 0 && Y > 0;
  const payment = valid ? (R === 0 ? A / N : (A * R) / (1 - Math.pow(1 + R, -N))) : NaN;
  const totalPaid = valid ? payment * N : NaN;
  const totalInterest = valid ? totalPaid - A : NaN;
  return (
    <ToolPage
      tool={tool}
      intro={<p>Estimate the monthly payment on a fixed-rate installment loan. Also shows the total interest you'll pay over the life of the loan.</p>}
      formula={<>Monthly payment M = P · r / (1 − (1 + r)^−n),  r = annual rate / 12,  n = years × 12.</>}
      examples={[
        `A ${formatMoney(20000, currency)} car loan at 7% APR for 5 years → about ${formatMoney(396, currency)}/month, ${formatMoney(3762, currency)} total interest.`,
        `A ${formatMoney(250000, currency)} mortgage at 5.5% for 30 years → about ${formatMoney(1419, currency)}/month.`,
      ]}
      faqs={[
        { q: 'Are fees or insurance included?', a: 'No — this is the base amortized payment. Real loan payments often include escrow (taxes, insurance) or lender fees.' },
        { q: 'What is APR vs interest rate?', a: 'The interest rate is what accrues on the balance; APR also includes certain fees, expressed on an annual basis. For most quick estimates, use the interest rate here.' },
      ]}
      limitations={<>Applies to fixed-rate, fully amortizing loans. Variable-rate or interest-only loans require different math.</>}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="grid grid-cols-2 gap-4 content-start">
          <Field label={`Loan amount (${currency.symbol})`} htmlFor="loan-amt"><input id="loan-amt" data-testid="loan-amt" type="number" step="any" value={amt} onChange={(e) => setAmt(e.target.value)} className={inputClass} /></Field>
          <Field label="Annual rate (%)" htmlFor="loan-rate"><input id="loan-rate" data-testid="loan-rate" type="number" step="any" value={rate} onChange={(e) => setRate(e.target.value)} className={inputClass} /></Field>
          <Field label="Term (years)" htmlFor="loan-years"><input id="loan-years" data-testid="loan-years" type="number" step="any" value={years} onChange={(e) => setYears(e.target.value)} className={inputClass} /></Field>
        </div>
        <div className="result-panel rounded-xl p-6 space-y-2 text-sm">
          <Row label="Monthly payment" value={formatMoney(payment, currency)} primary testId="loan-monthly" />
          <Row label="Total paid" value={formatMoney(totalPaid, currency)} testId="loan-total" />
          <Row label="Total interest" value={formatMoney(totalInterest, currency)} testId="loan-interest" />
        </div>
      </div>
    </ToolPage>
  );
}

// ========================= Tip =========================
export function TipCalculator() {
  const tool = getToolBySlug('tip-calculator');
  const { currency } = useCurrency();
  const [bill, setBill] = useState('55');
  const [pct, setPct] = useState('18');
  const [people, setPeople] = useState('2');
  const B = num(bill), P = num(pct), N = Math.max(1, parseInt(people) || 1);
  const tip = !isNaN(B) && !isNaN(P) ? (B * P) / 100 : NaN;
  const total = !isNaN(tip) ? B + tip : NaN;
  const perPerson = !isNaN(total) ? total / N : NaN;
  return (
    <ToolPage
      tool={tool}
      intro={<p>Add a tip percentage to a bill and split the total across any number of people. Adjust the tip up or down in one tap.</p>}
      formula={<>Tip = Bill × %,  Total = Bill + Tip,  Each = Total / People</>}
      examples={[
        `A ${formatMoney(55, currency)} dinner at 18% tip → ${formatMoney(9.9, currency)} tip, ${formatMoney(64.9, currency)} total.`,
        `Split ${formatMoney(120, currency)} between 4 people at a 20% tip → ${formatMoney(36, currency)} each.`,
      ]}
      faqs={[
        { q: 'What is a normal tip percentage?', a: 'It varies by country. In the US, 15–20% for sit-down restaurants is common; in many other countries, tipping is optional or already included as service charge.' },
        { q: 'Should I tip on the pre-tax or post-tax amount?', a: 'Enter whichever you prefer — most people tip on the post-tax bill for simplicity.' },
      ]}
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label={`Bill (${currency.symbol})`} htmlFor="tip-bill"><input id="tip-bill" data-testid="tip-bill" type="number" step="any" value={bill} onChange={(e) => setBill(e.target.value)} className={inputClass} /></Field>
            <Field label="People" htmlFor="tip-people"><input id="tip-people" data-testid="tip-people" type="number" min="1" step="1" value={people} onChange={(e) => setPeople(e.target.value)} className={inputClass} /></Field>
          </div>
          <Field label={`Tip (${pct}%)`} htmlFor="tip-pct">
            <input id="tip-pct" data-testid="tip-pct" type="range" min="0" max="30" step="1" value={pct} onChange={(e) => setPct(e.target.value)} className="w-full accent-emerald-600" />
            <div className="flex gap-2 mt-2">
              {['10','15','18','20','25'].map((p) => (
                <button key={p} type="button" onClick={() => setPct(p)} data-testid={`tip-preset-${p}`}
                  className={`px-3 h-9 rounded-lg text-sm ${pct === p ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>{p}%</button>
              ))}
            </div>
          </Field>
        </div>
        <div className="result-panel rounded-xl p-6 space-y-2 text-sm">
          <Row label="Tip" value={formatMoney(tip, currency)} testId="tip-tip" />
          <Row label="Total" value={formatMoney(total, currency)} testId="tip-total" />
          <Row label={`Per person (÷ ${N})`} value={formatMoney(perPerson, currency)} primary testId="tip-per" />
        </div>
      </div>
    </ToolPage>
  );
}
